import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, Check, Megaphone, UserX } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { notificationTimestamp, unreadIdsToMark } from "@/lib/notificationDisplay";

export default function Notifications() {
  const { t } = useTranslation('notifications');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // 008: den tidigare realtime-kanalen här är BORTTAGEN. `notifications` ligger
  // inte i publikationen `supabase_realtime` (0 rader i pg_publication_tables),
  // så prenumerationen fyrade aldrig — den var död kod som fick nästa läsare att
  // tro att panelen uppdaterades i realtid. Badgen uppdateras av sin egen
  // 30-sekunderspoll plus explicit invalidering nedan.
  //
  // OBS: toasten för `rsvp_cancelled` som låg i kanalen försvinner med den. Den
  // har aldrig visats för någon användare av samma skäl.

  const { data: notifications, isLoading } = useQuery({
    // Nyckeln bär user-id: utan det delar två konton i samma webbläsarsession
    // cache-post, och nästa användare ser föregåendes notiser tills den
    // invalideras.
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // ── Läst-markering vid panelbesök (008) ──
  //
  // Klockan är inte en inkorg: öppnar man den ska pricken bort. Tidigare var den
  // ENDA skrivvägen ett klick på varje enskilt kort — och det klicket navigerade
  // dessutom bort användaren (alla refund-notiser bär action_url), så att "läsa"
  // sina notiser innebar att kastas in i ett inbjudningsflöde.
  //
  // Vi markerar bara de rader som FAKTISKT hämtats och renderats. Listan är
  // begränsad till 50 medan badgen räknar obegränsat — en blind "markera allt"
  // hade markerat notiser användaren aldrig sett.
  const markedRef = useRef(false);
  useEffect(() => {
    if (!user || !notifications || markedRef.current) return;
    const ids = unreadIdsToMark(notifications);
    markedRef.current = true;
    if (ids.length === 0) return;

    void (async () => {
      // .eq("user_id") är ett BÄRANDE säkerhetsfilter, inte defense-in-depth:
      // RLS-policyn "Admin org notifications" är FOR ALL och ger en org-admin
      // UPDATE på alla medlemmars notiser i hallen. Utan filtret skulle en
      // admin som öppnar sin panel markera andras notiser som lästa.
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .in("id", ids)
        .eq("user_id", user.id);

      if (error) {
        // Fel sväljs inte längre. Den gamla koden kastade bort { error }, så en
        // RLS-regression hade varit osynlig.
        console.error("[notifications] kunde inte markera som lästa:", error);
        markedRef.current = false;
        return;
      }

      // Badgen ligger på EGNA query-nycklar. Den gamla koden invaliderade
      // ["notifications"] — listan — och rörde aldrig badgen, som därför levde
      // kvar till nästa 30-sekunderspoll.
      queryClient.invalidateQueries({ queryKey: ["notifications-unread", user.id] });
      queryClient.invalidateQueries({ queryKey: ["coach-unread-cancellations", user.id] });
      // Listan invalideras MEDVETET INTE: korten ska behålla sin ny-markering
      // under besöket, annars försvinner all information om vad som var nytt i
      // samma ögonblick som man öppnar panelen.
    })();
  }, [user, notifications, queryClient]);

  return (
    <div className="space-y-4 pb-20">
      <h1 className="text-2xl font-bold">{t('title')}</h1>
      {isLoading ? (
        [1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)
      ) : notifications?.length ? (
        notifications.map((n) => (
          <Card
            key={n.id}
            className={cn(
              "transition-colors",
              // Kortet är klickbart bara när det finns någonstans att gå.
              n.action_url ? "cursor-pointer hover:bg-muted/30" : "cursor-default",
              !n.read && "border-primary/30 bg-primary/5",
              n.type === "admin_broadcast" && !n.read && "border-accent bg-accent/20",
              n.type === "rsvp_cancelled" && !n.read && "border-orange-300 bg-orange-50 dark:bg-orange-950/20"
            )}
            onClick={() => {
              // Klicket NAVIGERAR bara. Läsmarkeringen sköts av effekten ovan,
              // så man inte behöver lämna panelen för att bli av med prickan.
              if (n.action_url) navigate(n.action_url);
            }}
          >
            <CardContent className="flex items-start gap-3 p-4">
              <div className={cn(
                "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                n.read ? "bg-muted" : n.type === "admin_broadcast" ? "bg-accent" : "bg-primary/10"
              )}>
                {n.read ? (
                  <Check className="h-4 w-4 text-muted-foreground" />
                ) : n.type === "admin_broadcast" ? (
                  <Megaphone className="h-4 w-4 text-accent-foreground" />
                ) : n.type === "rsvp_cancelled" ? (
                  <UserX className="h-4 w-4 text-orange-500" />
                ) : (
                  <Bell className="h-4 w-4 text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn("text-sm", !n.read && "font-medium")}>{n.title}</p>
                {n.body && <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>}
                {/* Absolut tidsstämpel FÖRE den relativa. Bara relativ tid gjorde
                    två notiser 71 sekunder isär omöjliga att skilja åt — de skrevs
                    båda ut som "för 3 timmar sedan" och såg ut som en dubblett. */}
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="tabular-nums">{notificationTimestamp(n.created_at, new Date())}</span>
                  <span className="mx-1">·</span>
                  <span>{formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: sv })}</span>
                </p>
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <Bell className="mx-auto h-8 w-8 mb-2 opacity-50" />
          <p>{t('noNotifications')}</p>
        </div>
      )}
    </div>
  );
}
